import React, { useState } from 'react';

import { useParams } from 'react-router-dom';

import ActionDropDown from '~/components/ActionDropDown/ActionDropDown';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import CobuSwitcher from '~/components/CobuSwitcher/CobuSwitcher';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import Spinner from '~/components/Spinner/Spinner';
import { useBuilding } from '~/hooks/building/useBuilding';
import CobuConstants from '~/helpers/cobuConstants';

import { ResidentUIModel } from '~/types/residentManagement';
import { CellProps } from 'react-table';
import MoreActions from '~/components/MoreActions/MoreActions';
import Spacer from '~/components/Spacer/Spacer';
import GenericModal from '~/components/GenericModal/GenericModal';
import CreateResidentForm from '~/components/CreateResidentForm/CreateResidentForm';
import ImportResidentsModal from '~/components/ImportResidentsModal/ImportResidentsModal';
import useResident from '~/hooks/resident/useResident';

import classes from './ResidentManagement.module.css';
import { getCreateUserFormConfig } from '~/helpers/formHelpers';
import { withFullScreenLoader } from '~/hocs/withFullScreenLoader';
import { useFullScreenLoader } from '~/hooks/useFullScreenLoader';
import { Tabs } from '~/components/Tabs';
import { toast } from 'react-toastify';
import { createAdminsRequest } from '~/api/admin';
import { pauseResidentOutreachRequest } from '~/api/building';
import { Roles } from '~/enums/Roles';
import BlockUserModal from '~/components/BlockUserModal/BlockUserModal';
import { useSelector } from 'react-redux';
import { RootState } from '~/redux/store';
 
const ResidentManagement = () => {
  /* Params */
  const { buildingId } = useParams<{ buildingId: string }>();
  const { data: selectedBuilding } = useBuilding(buildingId);
  const [selectedUserStatusState, setselectedUserStatusState] = useState<
    'ACTIVE' | 'DEACTIVATED' | string
  >('ACTIVE');
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseResidentId, setPauseResidentId] = useState('');
  const [pauseUntilDate, setPauseUntilDate] = useState('');

  /* Data logics */
  const {
    residentState,
    residentTableConfig,

    createBuildingResident,
    deactivateBuildingResident,
    deactivateMultipleBuildingResident,
    activateBuildingResident,
    activateMultipleBuildingResident,
    welcomeMultipleBuildingResident,
    sendReviewInvitationForResident,
    removeFromReviewOutreach,
    verifyResidentPhoneNumber,
    restrictBuildingResident,
    unrestrictBuildingResident,
    buildingTier,

    toggleActiveState,
    showHideUserCreateModal,
    isCreatePMUser,
    downloadCsv,
    downloading,
    refreshData
  } = useResident(buildingId);
  const [showImportModal, setShowImportModal] = useState(false);
  const { loader } = useFullScreenLoader();

  const [blockResidentTarget, setBlockResidentTarget] =
    useState<ResidentUIModel | null>(null);

  React.useEffect(() => {
    if (downloading.users || downloading.tenants) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [downloading.users, downloading.tenants]);

  const { userRole } = useSelector((state: RootState) => ({
    userRole: state.auth.userRole
  }));

  // Mirrors backend can-restrict-resident. Hidden until the tier loads.
  const isPmRole = userRole === Roles.PM || userRole === Roles.PM_FREE;
  const isVcmOrSuperadmin =
    userRole === Roles.Vcm || userRole === Roles.Superadmin;
  const canBlock =
    !!buildingTier &&
    (isVcmOrSuperadmin || (isPmRole && buildingTier.allowPmBlocking));

  const sendReviewInvitationForResidentWithSms = (user: string) => {
    loader.show();
    sendReviewInvitationForResident({ user, sendInvitationSms: true }).finally(
      () => loader.hide()
    );
  };

  const sendReviewInvitationForResidentWithoutSms = (user: string) => {
    loader.show();
    sendReviewInvitationForResident({ user, sendInvitationSms: false }).finally(
      () => loader.hide()
    );
  };

  const removeFromGoogleReviewOutreach = (user: string) => {
    loader.show();

    removeFromReviewOutreach(user).finally(() => loader.hide());
    toast('Resident has been removed from future outreach');
  };

  const handlePauseResident = () => {
    loader.show();
    pauseResidentOutreachRequest({
      user: pauseResidentId,
      pausedUntil: new Date(pauseUntilDate).toISOString()
    })
      .then(() => {
        toast('Resident has been paused from review outreach');
        setShowPauseModal(false);
        setPauseResidentId('');
        setPauseUntilDate('');
      })
      .finally(() => loader.hide());
  };

  /* UI Components */
  const createUserAction = () => {
    return userRole !== Roles.PM && userRole !== Roles.PM_FREE
      ? [
          <button
            className='btn-primary btn-standard bold'
            onClick={() => {
              isCreatePMUser.current = false;

              showHideUserCreateModal();
            }}
          >
            {CobuConstants.residentManagement.createUser}
          </button>,
          <button
            className='btn-primary btn-standard bold'
            onClick={() => setShowImportModal(true)}
          >
            {'Create Residents using CSV'}
          </button>,
          <button
            className='btn-primary btn-standard bold'
            onClick={() => {
              isCreatePMUser.current = true;

              showHideUserCreateModal();
            }}
          >
            {'Create PM'}
          </button>,
          <button
            className='btn-primary btn-standard bold'
            disabled={downloading.users}
            onClick={() => downloadCsv('users', selectedBuilding?.name)}
          >
            {downloading.users ? 'Downloading…' : 'Download Users'}
          </button>,
          <button
            className='btn-primary btn-standard bold'
            disabled={downloading.tenants}
            onClick={() => downloadCsv('tenants', selectedBuilding?.name)}
          >
            {downloading.tenants ? 'Downloading…' : 'Download Tenants'}
          </button>
        ]
      : [];
  };

  const getCobuTableInstance = (
    data: ResidentUIModel[],
    placeholder: string
  ) => {
    return (
      <CobuTableAdvanced
        columns={[
          ...residentTableConfig,
          // Block-only (PM) users only have actions on the ACTIVE tab, so no empty menu on Deactivated.
          ...(isVcmOrSuperadmin ||
          (canBlock && selectedUserStatusState === 'ACTIVE')
            ? [
                {
                  id: 'more',
                  Cell: (cell: CellProps<ResidentUIModel>) => (
                    <MoreActions
                      title={CobuConstants.residentManagement.actions}
                      options={
                        selectedUserStatusState === 'ACTIVE'
                          ? [
                              ...(isVcmOrSuperadmin
                                ? [
                                    {
                                      label:
                                        CobuConstants.residentManagement
                                          .deactivate,
                                      action: () => {
                                        deactivateBuildingResident(
                                          cell.row.original.uuid
                                        );
                                      }
                                    },
                                    ...(!cell.row.original.verifiedPhone
                                      ? [
                                          {
                                            label:
                                              CobuConstants.residentManagement
                                                .verifyPhone,
                                            action: () => {
                                              verifyResidentPhoneNumber(
                                                cell.row.original.uuid
                                              );
                                            }
                                          }
                                        ]
                                      : []),
                                    {
                                      label:
                                        CobuConstants.residentManagement
                                          .sendWelcomeMail,
                                      action: () => {
                                        welcomeMultipleBuildingResident([
                                          cell.row.original.uuid
                                        ]);
                                      }
                                    },
                                    {
                                      label:
                                        CobuConstants.residentManagement
                                          .sendReviewInvitationWithSms,
                                      action: () => {
                                        sendReviewInvitationForResidentWithSms(
                                          cell.row.original.uuid
                                        );
                                      }
                                    },
                                    {
                                      label:
                                        CobuConstants.residentManagement
                                          .sendReviewInvitationWithoutSms,
                                      action: () => {
                                        sendReviewInvitationForResidentWithoutSms(
                                          cell.row.original.uuid
                                        );
                                      }
                                    },
                                    {
                                      label:
                                        CobuConstants.residentManagement
                                          .removeFromReviewOutreach,
                                      action: () => {
                                        removeFromGoogleReviewOutreach(
                                          cell.row.original.uuid
                                        );
                                      }
                                    },
                                    {
                                      label:
                                        CobuConstants.residentManagement
                                          .pauseFromReviewOutreach,
                                      action: () => {
                                        setPauseResidentId(
                                          cell.row.original.uuid
                                        );
                                        setShowPauseModal(true);
                                      }
                                    }
                                  ]
                                : []),
                              ...(canBlock
                                ? [
                                    cell.row.original.restricted
                                      ? {
                                          label: 'Unblock user',
                                          action: () => {
                                            unrestrictBuildingResident(
                                              cell.row.original.uuid
                                            );
                                          }
                                        }
                                      : {
                                          label: 'Block user',
                                          action: () => {
                                            setBlockResidentTarget(
                                              cell.row.original
                                            );
                                          }
                                        }
                                  ]
                                : [])
                            ]
                          : isVcmOrSuperadmin
                          ? [
                              {
                                label:
                                  CobuConstants.residentManagement.activate,
                                action: () => {
                                  activateBuildingResident(
                                    cell.row.original.uuid
                                  );
                                }
                              }
                            ]
                          : []
                      }
                    />
                  )
                }
              ]
            : [])
        ]}
        data={data}
        noData={{
          label: placeholder,
          action: undefined,
          image: undefined
        }}
        search={{
          searchFields: ['firstName', 'phone'],
          placeholder: 'Search by name or phone'
        }}
        actions={createUserAction()}
        actionsStyle={{ top: '160px' }}
      />
    );
  };

  const createResidentModal = () => {
    return (
      residentState.showCreateModal && (
        <GenericModal
          showModal={residentState.showCreateModal}
          title={
            isCreatePMUser.current
              ? CobuConstants.residentManagement.createPMIn(
                  selectedBuilding?.name
                )
              : CobuConstants.residentManagement.createresidentsIn(
                  selectedBuilding?.name
                )
          }
          content={
            <CreateResidentForm
              formConfig={getCreateUserFormConfig(isCreatePMUser.current)}
              cancel={() => {
                showHideUserCreateModal();

                isCreatePMUser.current = false;
              }}
              createResident={(resident) => {
                createBuildingResident({
                  ...resident,
                  building: buildingId,
                  verified: true,
                  verifiedPhone: true,
                  isPMUser: isCreatePMUser.current,
                  markAsPMCommentor: resident.doNotDeactivate
                });
              }}
              checkboxTitle={
                isCreatePMUser.current
                  ? 'Make building user'
                  : CobuConstants.createResident.doNotDeactivate
              }
            />
          }
          hideActionButton
          canceActionButton
          actionText={CobuConstants.residentManagement.createActionTitle}
          actionFunction={() => {}}
          onChange={() => {}}
        />
      )
    );
  };

  if (residentState.loading)
    return (
      <div className={classes.loadingContainer}>
        <Spinner />
        <h3 style={{ textAlign: 'center' }}>{residentState.loadingString}</h3>
      </div>
    );

  return (
    <div className='content-container'>
      <Breadcrumbs />
      <h1>
        {CobuConstants.residentManagement.residentsIn(selectedBuilding?.name)}
      </h1>
      <div className='margin-bottom-16' style={{ width: '340px' }}>
        <Tabs.Container
          value={selectedUserStatusState}
          onChange={setselectedUserStatusState}
        >
          <Tabs.Option
            selected={selectedUserStatusState == 'ACTIVE'}
            value='ACTIVE'
          >
            Active Residents
          </Tabs.Option>
          <Tabs.Option
            selected={selectedUserStatusState == 'DEACTIVATED'}
            value='DEACTIVATED'
          >
            Deactivated Residents
          </Tabs.Option>
        </Tabs.Container>
      </div>
      {selectedUserStatusState == 'ACTIVE'
        ? getCobuTableInstance(
            residentState.activeResidents,
            CobuConstants.residentManagement.noActiveUsers
          )
        : getCobuTableInstance(
            residentState.deactivatedResidents,
            CobuConstants.residentManagement.noDeactivatedUsers
          )}
      {userRole !== Roles.PM && userRole !== Roles.PM_FREE && (
        <ActionDropDown
          title={CobuConstants.residentManagement.selectAction}
          options={
            selectedUserStatusState == 'ACTIVE'
              ? [
                  {
                    label: CobuConstants.residentManagement.deactivate,
                    action: () => {
                      deactivateMultipleBuildingResident(
                        residentState.selectedIds.activeSelectedIds
                      );
                    }
                  },
                  {
                    label: CobuConstants.residentManagement.sendWelcomeMail,
                    action: () => {
                      welcomeMultipleBuildingResident(
                        residentState.selectedIds.activeSelectedIds
                      );
                    }
                  }
                ]
              : [
                  {
                    label: CobuConstants.residentManagement.activate,
                    action: () => {
                      activateMultipleBuildingResident(
                        residentState.selectedIds.deactivatedSelectedIds
                      );
                    }
                  }
                ]
          }
        />
      )}
      <Spacer height={80} />
      {createResidentModal()}
      {showImportModal && (
        <ImportResidentsModal
          buildingId={buildingId}
          buildingName={selectedBuilding?.name}
          onClose={(createdAny) => {
            setShowImportModal(false);
            if (createdAny) refreshData();
          }}
        />
      )}
      <GenericModal
        showModal={showPauseModal}
        title='Pause from review outreach'
        content={
          <div>
            <p>Select a date to pause outreach until:</p>
            <input
              type='date'
              value={pauseUntilDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setPauseUntilDate(e.target.value)}
            />
          </div>
        }
        actionText='Pause outreach'
        actionButtonDisabled={!pauseUntilDate}
        actionFunction={handlePauseResident}
        onChange={() => {
          setShowPauseModal(false);
          setPauseResidentId('');
          setPauseUntilDate('');
        }}
      />
      {blockResidentTarget && (
        <BlockUserModal
          showModal={!!blockResidentTarget}
          firstName={blockResidentTarget.firstName}
          isIntegratedBuilding={!!buildingTier?.isIntegrated}
          onClose={() => setBlockResidentTarget(null)}
          onConfirm={(payload) => {
            const target = blockResidentTarget;
            setBlockResidentTarget(null);
            if (target) {
              restrictBuildingResident(target.uuid, payload);
            }
          }}
        />
      )}
    </div>
  );
};

export default withFullScreenLoader(ResidentManagement);
